import React, { useRef, useState } from "react";
import { Volume2, VolumeX, ArrowRight, AlertTriangle } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { VIDEOS, POSTERS } from "@/lib/business";
import { useVoiceOver } from "@/lib/useVoiceOver";

export default function Hero() {
  const { t, lang } = useI18n();
  const videoRef = useRef(null);
  const { speak, stop, speaking, supported } = useVoiceOver();
  const [muted, setMuted] = useState(true);

  const toggleVoice = () => {
    if (speaking) {
      stop();
    } else {
      speak(t.hero.narration, lang);
    }
  };

  return (
    <section
      data-testid="hero-section"
      className="relative min-h-[90vh] w-full overflow-hidden bg-[#050505]"
    >
      {VIDEOS.hero ? (
        <video
          ref={videoRef}
          data-testid="hero-video"
          autoPlay
          muted={muted}
          loop
          playsInline
          poster={POSTERS.hero}
          className="absolute inset-0 w-full h-full object-cover opacity-70"
        >
          <source src={VIDEOS.hero} type="video/mp4" />
        </video>
      ) : (
        <div
          data-testid="hero-poster"
          className="absolute inset-0 w-full h-full bg-cover bg-center opacity-80 animate-kenburns"
          style={{ backgroundImage: `url(${POSTERS.hero})` }}
        />
      )}

      <div className="absolute inset-0 hero-overlay" />
      <div className="absolute inset-0 bg-grain pointer-events-none" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 pt-32 pb-24 min-h-[90vh] flex flex-col justify-center">
        <div className="animate-fade-up max-w-4xl">
          <div className="flex items-center gap-3 mb-8">
            <span className="w-12 h-px bg-[#e52620]" />
            <span
              data-testid="hero-tagline"
              className="text-[11px] uppercase tracking-[0.3em] text-zinc-300 font-bold font-mono"
            >
              {t.hero.tagline}
            </span>
          </div>

          <h1
            data-testid="hero-title"
            className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-black uppercase tracking-tighter leading-[0.9] text-white text-balance"
          >
            <span className="block">{t.hero.title_1}&nbsp;</span>
            <span className="block">
              <span className="text-[#e52620]">
                {t.hero.title_2.split(" ")[0]}
              </span>
              <span> </span>
              <span className="italic font-light">
                {t.hero.title_2.split(" ").slice(1).join(" ")}
              </span>
            </span>
          </h1>

          <p
            data-testid="hero-subtitle"
            className="mt-8 text-lg md:text-xl text-zinc-300 max-w-2xl leading-relaxed"
          >
            {t.hero.subtitle}
          </p>

          <div className="mt-12 flex flex-wrap items-center gap-4">
            <a
              href="#accident"
              data-testid="hero-cta-primary"
              className="group inline-flex items-center gap-3 bg-[#e52620] hover:bg-[#c10015] text-white px-7 py-4 rounded-md font-bold uppercase tracking-[0.12em] text-sm transition-all active:scale-95 glow-red"
            >
              <AlertTriangle className="w-4 h-4" />
              {t.hero.cta_primary}
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </a>
            <a
              href="#services"
              data-testid="hero-cta-secondary"
              className="inline-flex items-center gap-3 bg-white/5 hover:bg-white/10 text-white border border-white/15 hover:border-white/30 px-7 py-4 rounded-md font-bold uppercase tracking-[0.12em] text-sm transition-all"
            >
              {t.hero.cta_secondary}
            </a>

            {supported && (
              <button
                data-testid="hero-voiceover-toggle"
                onClick={toggleVoice}
                aria-label={speaking ? t.hero.voiceover_on : t.hero.voiceover_off}
                aria-pressed={speaking}
                className={`inline-flex items-center justify-center w-14 h-14 rounded-md transition-all border ${
                  speaking
                    ? "bg-white text-black border-white"
                    : "bg-white/5 text-zinc-300 border-white/15 hover:border-white/40 hover:text-white"
                }`}
              >
                {speaking ? (
                  <Volume2 className="w-5 h-5 animate-pulse" />
                ) : (
                  <VolumeX className="w-5 h-5" />
                )}
              </button>
            )}
          </div>
        </div>

        <div className="absolute bottom-12 left-0 right-0 px-6 md:px-12">
          <div className="max-w-7xl mx-auto flex flex-wrap items-end justify-between gap-6 text-zinc-500 text-xs uppercase tracking-[0.25em] font-mono">
            <div data-testid="hero-info-location">
              Milchgasse 4 · 4628 Wolfwil
            </div>
            <div className="hidden md:flex items-center gap-6">
              <span>Inh. Dominic Müller</span>
              <span>·</span>
              <span>UID CHE-339.965.996</span>
              <span>·</span>
              <span className="text-zinc-300">Scroll ↓</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
